import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { RHeader, RPage, RViewer } from '../../../../components/PdfRender';
import { useCompany } from '../../../../hooks/company';
import { formatDate } from '../../../utils/format-date';
import { currency } from '../../../utils/functions';
import { styles } from './styles';

type TReportKey =
  | 'unified'
  | 'synthetic'
  | 'taxes'
  | 'professionals'
  | 'commissions';

interface IFilterEntry {
  label: string;
  value: string;
}

interface IAdvancedFinancialReportProps {
  reportKey: TReportKey;
  reportLabel: string;
  description: string;
  items: any[];
  summary: Record<string, number>;
  filters: IFilterEntry[];
}

const SUMMARY_LABELS: Record<string, string> = {
  total_financials: 'Lançamentos',
  total_entries: 'Entradas',
  total_exits: 'Saídas',
  total_discounts: 'Descontos',
  total_taxes: 'Taxas',
  net_result: 'Resultado líquido',
  total_groups: 'Grupos',
  total_gross: 'Valor bruto',
  total_methods: 'Formas',
  effective_rate: 'Taxa efetiva',
  total_professionals: 'Profissionais',
  total_treatments: 'Tratamentos',
  total_items: 'Itens',
  total_production: 'Produção',
  total_base: 'Base comissionável',
  total_commissions: 'Comissões',
};

const formatSummaryValue = (key: string, value: number) => {
  if (key.includes('rate') || key.includes('percent')) {
    return `${Number(value || 0).toFixed(2)}%`;
  }

  if (
    key.includes('total_financials') ||
    key.includes('total_groups') ||
    key.includes('total_methods') ||
    key.includes('total_professionals') ||
    key.includes('total_treatments') ||
    key.includes('total_items')
  ) {
    return Number(value || 0).toString();
  }

  return currency(Number(value || 0));
};

const getColumns = (reportKey: TReportKey) => {
  switch (reportKey) {
    case 'unified':
      return ['Código', 'Emissão', 'Cliente', 'Tipo', 'Status', 'Valor'];
    case 'synthetic':
      return ['Plano', 'Tipo', 'Qtd.', 'Bruto', 'Descontos', 'Taxas'];
    case 'taxes':
      return ['Forma', 'Qtd.', 'Base', 'Taxas', 'Taxa efetiva'];
    case 'professionals':
      return [
        'Profissional',
        'Comissão',
        'Trat.',
        'Itens',
        'Produção',
        'Ticket',
      ];
    case 'commissions':
      return [
        'Profissional',
        'Comissão',
        'Trat.',
        'Itens',
        'Base',
        'Comissão apurada',
      ];
    default:
      return [];
  }
};

const getRowValues = (reportKey: TReportKey, item: any) => {
  switch (reportKey) {
    case 'unified':
      return [
        `#${item.code || '-'}`,
        item.issue_date ? formatDate(item.issue_date) : '-',
        item.client_name || '-',
        Number(item.type) === 1 ? 'Receita' : 'Despesa',
        item.status === 2 ? 'Pago' : item.status === 5 ? 'Cancelado' : 'Aberto',
        currency(Number(item.value || 0)),
      ];
    case 'synthetic':
      return [
        item.group_name || 'Sem plano',
        item.financial_type || '-',
        Number(item.quantity || 0).toString(),
        currency(Number(item.gross_value || 0)),
        currency(Number(item.discount_value || 0)),
        currency(Number(item.taxe_value || 0)),
      ];
    case 'taxes':
      return [
        item.payment_method || 'Sem forma',
        Number(item.quantity || 0).toString(),
        currency(Number(item.gross_value || 0)),
        currency(Number(item.taxe_value || 0)),
        `${Number(item.effective_rate || 0).toFixed(2)}%`,
      ];
    case 'professionals':
      return [
        item.professional_name || '-',
        `${Number(item.commission_percent || 0).toFixed(2)}%`,
        Number(item.total_treatments || 0).toString(),
        Number(item.total_items || 0).toString(),
        currency(Number(item.production_value || 0)),
        currency(Number(item.average_ticket || 0)),
      ];
    case 'commissions':
      return [
        item.professional_name || '-',
        `${Number(item.commission_percent || 0).toFixed(2)}%`,
        Number(item.total_treatments || 0).toString(),
        Number(item.total_items || 0).toString(),
        currency(Number(item.base_value || 0)),
        currency(Number(item.commission_value || 0)),
      ];
    default:
      return [];
  }
};

const AdvancedFinancialReport: React.FC<IAdvancedFinancialReportProps> = ({
  reportKey,
  reportLabel,
  description,
  items,
  summary,
  filters,
}) => {
  const { company } = useCompany();
  const columns = getColumns(reportKey);
  const summaryEntries = Object.entries(summary || {});

  return (
    <RViewer>
      <RPage size="A4" orientation="landscape">
        <View style={styles.page}>
          <RHeader company={company} />

          <View style={styles.header}>
            <Text style={styles.title}>{reportLabel}</Text>
            <Text style={styles.subtitle}>{description}</Text>
          </View>

          {filters.length ? (
            <View style={styles.filters}>
              {filters.map((filter) => (
                <View key={filter.label} style={styles.filterBox}>
                  <Text style={styles.filterLabel}>{filter.label}</Text>
                  <Text style={styles.filterValue}>{filter.value}</Text>
                </View>
              ))}
            </View>
          ) : null}

          {summaryEntries.length ? (
            <View style={styles.summaryGrid}>
              {summaryEntries.map(([key, value]) => (
                <View key={key} style={styles.summaryCard}>
                  <Text style={styles.summaryLabel}>
                    {SUMMARY_LABELS[key] || key}
                  </Text>
                  <Text style={styles.summaryValue}>
                    {formatSummaryValue(key, Number(value || 0))}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          <View style={styles.table}>
            <View style={[styles.row, styles.headerRow]}>
              {columns.map((column) => (
                <Text key={column} style={[styles.cell, styles.headerCell]}>
                  {column}
                </Text>
              ))}
            </View>

            {items.map((item, index) => (
              <View
                key={String(
                  item.id ||
                    item.professional_id ||
                    item.group_name ||
                    item.payment_method ||
                    index,
                )}
                style={
                  index % 2 === 0 ? styles.row : [styles.row, styles.rowAlt]
                }
              >
                {getRowValues(reportKey, item).map((value, valueIndex) => (
                  <Text key={`${index}-${valueIndex}`} style={styles.cell}>
                    {value}
                  </Text>
                ))}
              </View>
            ))}
          </View>

          <Text
            style={styles.footer}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
            fixed
          />
        </View>
      </RPage>
    </RViewer>
  );
};

export default AdvancedFinancialReport;
